import { PORTFOLIO_ASSET_ALLOCATION } from '../../../enums/AssetAllocation';
import {
  CREDIT_QUALITY_BELOW_B_CATEGORIES,
  CREDIT_QUALITY_CATEGORIES,
  DEFAULT_GROWTH_VALUES,
  TIME_PERIODS,
  ASSET_ALLOCATION_GROUPS,
  ASSET_GROUPS,
  DEFAULT_CORRELATION_OPTIONS,
  COMPARISON_ITEM_TYPES,
} from '../../../utils/constants';
import { PROPOSAL_DISCLAIMERS } from '../utils/constants';
import { formatLocalizedString, formatMessageWithLocale, generateLocalizedStringFromText, LOCALE } from '../../../utils/i18n';
import { deepClone } from '../../../utils/objects';
import { calculateDrawdownData, calculateTrailingReturns, DrawdownData, getVarianceMetrics, trimRollingData } from '../../../utils/calculation-utils';
import { DISCLAIMER_KEYS } from '../../../utils/disclaimers/constants';
import { RiskProfileRule, RiskProfileRuleType, RiskProfileAssetAllocationRule, InvestmentAssetType } from '../../../types/risk-profiles';
import { MERBreakdown } from './constants';
import { DAOType, Proposal, ProposalItemType } from '../Proposal';
import { getCouponDates } from '../utils/bonds-utils';
import { CARBON_INTENSITY, TEMPERATURE_RANKING, CarbonIntensityLimit, ImpliedTemperatureLimit } from '../../../enums/Esg';
import Dictionary from '@/types/dictionary';
import Timestamp from '@/types/timestamp';
import ChartItem from '@/types/chart-item';
import LineSeriesDataPoint from '@/types/line-series-data-point';
import GrowthTypes from '@/types/growth-types';
import RiskPeriods from '@/types/risk-periods';
import ComparisonItem from '@/types/comparison-item';
import AllocationDetails from '@/types/allocation-details';
import LocalizedAllocationDetails from '@/types/localized-allocation-details';
import ComparisonHolding from '@/types/comparison-holding';
import LocalizedString from '@/types/localized-string';
import RiskMetric, { CorrelationMatrixOptions } from '@/types/risk-metric';
import CorrelationData from '@/types/correlation-data';
import CorrelationMetric from '@/types/correlation-metric';
import RiskProfileItem from '@/types/risk-profile-item';
import MinMax from '@/types/min-max';
import TimePeriods from '@/types/time-periods';
import BestWorstMetric from '@/types/best-worst-metric';
import MinInvestmentInfo from '@/types/min-investment-info';
import RiskRating from '@/types/risk-rating';
import RollingIntervals from '@/types/rolling-intervals';
import PercentGrowth from '@/types/percent-growth';
import FundAllocation from '@/types/fund-allocation';
import IncomeStreamScenarios from '@/types/income-stream-scenarios';
import RebalanceAnalysisData from '@/types/rebalance-analysis-data';
import LipperLeaders from '@/types/lipper-leaders';
import ESGInstrument from '@/types/esg-instrument';
import { getCorrelationMatrix } from '@/utils/comparison/portfolio-comparison-item';
import { AltClass } from '@/types/models/alt';
import { HOLDING_TYPES } from '@/utils/portfolios';
import ComparisonAltMetrics from '@/types/comparison-alt-metrics';
import User from '@/types/user';
import { Account } from '@/types/planning';
import { getRiskProfileRuleData } from '@/utils/risk-profiles';
import { monthsDiff, monthsToPeriod } from '@/utils/time';
import HistoricalScenariosComparisonItemPerformance from '@/types/historical-scenarios-comparison-item-performance';
import HistoricalScenario from '@/types/historical-scenario';
import { PortfolioTaxMetrics } from '@/types/tax-analysis';

export interface DataPointResponse<T> {
  response: T;
  disclaimerKeys: Array<string>;
}

// The concept is: Response With an HasDisclaimer Flag.
// I Could not come up with a better balance between not to long and clear. Open to suggestions!
export interface ResponseHasDisclaimer<T> {
  value: T;
  hasDisclaimer?: boolean;
}

export type PercentGrowthHasDisclaimer = {
  [TKey in GrowthTypes]: ResponseHasDisclaimer<number | null>;
};

export type RiskMetricHasDisclaimer = {
  [TKey in RiskPeriods]: ResponseHasDisclaimer<number | null>;
};

export type ESGOverviewDetails = {
  classification?: string;
  score?: number;
  value?: string;
  peerRank?: number;
  globalRank?: number;
};

export type ESGCarbonIntensityDetails = {
  value: number | null;
  transposedValue: number | null;
  key: string;
};

export type ESGTemperatureDetails = {
  value: number | null;
  transposedValue: number | null;
  key: string;
};

export type AltMetricsDetails = ComparisonAltMetrics & { manufacturer?: string };

export class DataPoints {
  /**
   * Gets the asset allocation for an item by key (Canadian Equity ,US Equity, etc.)
   * @param item the item to retrieve the info for
   * @param key the key for the asset allocation to lookup
   */
  public static getDetailedAssetAllocationByKey(item: ComparisonItem, key: string): DataPointResponse<number> {
    const { allocations } = item;

    if (!allocations || !allocations.detailedAsset) {
      return { response: 0, disclaimerKeys: [] };
    }

    const alloc = allocations.detailedAsset.find(asset => asset.name[LOCALE.EN_CA] === key);
    return {
      response: (alloc && alloc.weight) || 0,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.ALLOCATIONS_AS_AT, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the Mixed Assets allocation portion for an item
   * @param {ComparisonItem} item the item to retrieve the Mixed Assets allocation for
   * @returns {number} the weight percentage of Mixed Assets allocation, or 0 if not found
   */
  public static getMixedAssetAllocationPortion(item: ComparisonItem): number {
    const mixedAssetsItem = item?.allocations?.detailedAsset?.find(
      item => item.name && formatLocalizedString(item.name) === formatMessageWithLocale('AssetGroups.MIXED_ASSETS'),
    );

    const mixedAssetsWeight = mixedAssetsItem?.weight || 0;

    return mixedAssetsWeight;
  }

  /**
   * Gets the asset allocation for an item by key (Canadian Equity ,US Equity, etc.)
   * @param item the item to retrieve the info for
   * @param key the key for the asset allocation to lookup
   */
  public static getItemMissingAllocationPortion(item: ComparisonItem): number {
    const { allocations } = item;

    if (!allocations || !allocations.asset) {
      return 100;
    }

    const totalAllocation = Object.keys(allocations.asset).reduce((total, curr) => {
      let runningTotal = total;
      runningTotal += (allocations.asset && allocations.asset[curr]) || 0;
      return runningTotal;
    }, 0);
    if (totalAllocation < 100) {
      return 100 - totalAllocation;
    }
    return 0;
  }

  /**
   * Gets an asset allocation by key (this uses the asset allocations where the fixed
   * income is rolled into one category)
   * @param {ComparisonItem} item the proposal item
   * @param {string} key the key to retrieve the data for (Canadian Equity , Fixed Income ,etc)
   */
  public static getAssetAllocationByKey(item: ComparisonItem, key: string): DataPointResponse<number> {
    const { allocations } = item;

    if (allocations && allocations.asset && allocations.asset[key]) {
      return {
        response: allocations.asset[key] || 0,
        disclaimerKeys: [PROPOSAL_DISCLAIMERS.ALLOCATIONS_AS_AT, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
      };
    }

    return { response: 0, disclaimerKeys: [] };
  }

  /**
   * Gets an ESG rating asset allocation by key
   * @param {ComparisonItem} item the proposal item
   * @param {string} key the key to retrieve the data for ESG rating
   */
  public static getESGRatingAllocationByKey(item: ComparisonItem, key: string): DataPointResponse<number> {
    const { esg } = item;

    if (esg && esg.rating?.allocations && esg.rating?.allocations.length) {
      const allocation = esg.rating?.allocations.find(item => item.name === key);

      return {
        response: allocation?.weight || 0,
        disclaimerKeys: [PROPOSAL_DISCLAIMERS.ESG_OVERVIEW, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
      };
    }

    return { response: 0, disclaimerKeys: [] };
  }

  /**
   * Gets the Localized Country Allocations for an item
   * @param {ComparisonItem} item the item to retrieve the info for
   */
  public static getCountryAllocationsKeys(item: ComparisonItem): string[] {
    const { allocations } = item;

    if (!allocations || !allocations.country) {
      return [];
    }

    return allocations.country.map(alloc => alloc.name);
  }

  /**
   * Gets the Localized Country Allocations for an item
   * @param {ComparisonItem} item the item to retrieve the info for
   */
  public static getCountryAllocations(item: ComparisonItem): DataPointResponse<AllocationDetails[]> {
    const { allocations } = item;

    if (!allocations || !allocations.country) {
      return { response: [], disclaimerKeys: [] };
    }

    return { response: allocations.country, disclaimerKeys: [PROPOSAL_DISCLAIMERS.ALLOCATIONS_AS_AT, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER] };
  }

  /**
   * Gets the country allocation by a specific country name
   * @param {ComparisonItem} item the item to retrieve the info for
   * @param {string} key the key for the country name to lookup
   */
  public static getCountryAllocationByKey(item: ComparisonItem, countryName: string): DataPointResponse<number> {
    const { allocations } = item;

    if (!allocations || !allocations.country) {
      return { response: 0, disclaimerKeys: [] };
    }

    const match = allocations.country.find(ca => ca.name === countryName);
    return {
      response: (match && match.weight) || 0,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.ALLOCATIONS_AS_AT, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the region allocation by a specific region name
   * (regardless of equity or fixed income this is the overall region)
   * @param {ComparisonItem} item the item to retrieve the info for
   * @param {string} key the key for the region name to lookup
   */
  public static getRegionAllocationByKey(item: ComparisonItem, region: string): DataPointResponse<number> {
    const { allocations } = item;

    if (!allocations || !allocations.region) {
      return { response: 0, disclaimerKeys: [] };
    }

    const match = allocations.region.find(ca => ca.name === region);
    return {
      response: (match && match.weight) || 0,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.ALLOCATIONS_AS_AT, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the region allocation by a specific region name
   * (regardless of equity or fixed income this is the overall region)
   * @param {ComparisonItem} item the item to retrieve the info for
   * @param {string} key the key for the region/country name to lookup
   * @param {Dictionary<string[]} exclusionMap the exclusion map with the region and corresponding countries to remove
   */
  public static getRegionAllocationByWithCountry(item: ComparisonItem, key: string, exclusionMap: Dictionary<string[]>): DataPointResponse<number> {
    const { allocations } = item;
    if (!allocations || !allocations.region || !allocations.country) {
      return { response: 0, disclaimerKeys: [] };
    }
    const copy = allocations.region.map(ra => ({ name: ra.name, weight: ra.weight }));
    const adjustedAllocations = copy.reduce((acc, current) => {
      const currentItem = { ...current };
      if (exclusionMap[currentItem.name]) {
        const countries = exclusionMap[currentItem.name];
        const countryAllocations = allocations.country || [];
        const weights = countryAllocations.filter(ca => countries.includes(ca.name)).reduce((sum, c) => sum + c.weight, 0);
        currentItem.weight -= weights;
        for (const country of countries) {
          acc.push({ name: country, weight: DataPoints.getCountryAllocationByKey(item, country).response });
        }
      }
      if (currentItem.weight > 0) {
        acc.push(currentItem);
      }
      return acc;
    }, [] as AllocationDetails[]);
    const match = adjustedAllocations.find(regionAllocation => regionAllocation.name === key);
    return {
      response: (match && match.weight) || 0,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.ALLOCATIONS_AS_AT, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the equity country allocation by a specific country name
   * @param {ComparisonItem} item the item to retrieve the info for
   * @param {string} key the key for the country name to lookup
   */
  public static getEquityCountryAllocationByKey(item: ComparisonItem, countryName: string): DataPointResponse<number> {
    const { allocations } = item;

    if (!allocations || !allocations.equityCountry) {
      return { response: 0, disclaimerKeys: [] };
    }

    const match = allocations.equityCountry.find(ca => ca.name === countryName);
    return {
      response: (match && match.weight) || 0,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.ALLOCATIONS_AS_AT, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the fixed income country allocation by a specific country name
   * @param {ComparisonItem} item the item to retrieve the info for
   * @param {string} key the key for the country name to lookup
   */
  public static getFixedIncomeCountryAllocationByKey(item: ComparisonItem, countryName: string): DataPointResponse<number> {
    const { allocations } = item;

    if (!allocations || !allocations.fixedIncomeCountry) {
      return { response: 0, disclaimerKeys: [] };
    }

    const match = allocations.fixedIncomeCountry.find(ca => ca.name === countryName);
    return {
      response: (match && match.weight) || 0,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.ALLOCATIONS_AS_AT, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the top N country allocations (in order of allocation weight)
   * @param {ComparisonItem} item the item to retrieve the info for
   * @param {number} length the number of top X allocations to get
   */
  public static getTopNCountryAllocations(item: ComparisonItem, length: number): AllocationDetails[] {
    const { allocations } = item;

    if (!allocations || !allocations.country) {
      return [];
    }

    return allocations.country
      .map(sa => ({ name: sa.name, weight: sa.weight }))
      .sort((a, b) => b.weight - a.weight)
      .slice(0, length);
  }

  /**
   * Gets the top N sector allocations (in order of allocation weight)
   * @param {ComparisonItem} item the item to retrieve the info for
   * @param {number} length the number of top X allocations to get
   */
  public static getTopNSectorAllocations(item: ComparisonItem, length: number): LocalizedAllocationDetails[] {
    const { allocations } = item;

    if (!allocations || !allocations.sector) {
      return [];
    }

    return allocations.sector
      .map(sa => ({ name: { ...sa.name }, weight: sa.weight }))
      .sort((a, b) => b.weight - a.weight)
      .slice(0, length);
  }

  /**
   * Gets the country allocation by a specific sectorName name
   * @param {ComparisonItem} item the item to retrieve the info for
   * @param {string} key the key for the sector name to lookup
   */
  public static getSectorAllocationByKey(item: ComparisonItem, sectorName: string): DataPointResponse<number> {
    const { allocations } = item;

    if (!allocations || !allocations.sector) {
      return { response: 0, disclaimerKeys: [] };
    }

    const match = allocations.sector.find(ca => ca.name[LOCALE.EN_CA] === sectorName);
    return {
      response: (match && match.weight) || 0,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.ALLOCATIONS_AS_AT, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Validate if an item type Alt or Fund is the same as another item but with a different series
   * @param {ComparisonHolding[]} holdings the holdings to check against
   * @param {ComparisonHolding} holding the holding to check
   */
  public static isSameItemWithDifferentSeries = (holdings: ComparisonHolding[], holding: ComparisonHolding): boolean => {
    if (!holdings?.length || !holding) {
      return false;
    }

    if (holding.type === HOLDING_TYPES.ALT) {
      return holdings.some(h => h.id === holding.id);
    }

    if (holding.type === HOLDING_TYPES.FUND) {
      return holdings.some(h => h.id === holding.id && h.symbol !== holding.symbol);
    }

    return false;
  };

  /**
   * Get the underlying holdings for an item
   * @param {ComparisonItem} item the item to retrieve the info for
   */
  public static getItemHoldings(item: ComparisonItem, hasClassEnabled?: boolean): DataPointResponse<ComparisonHolding[]> {
    const getLocalizedNameExtra = (holding: ComparisonHolding) => {
      if (holding.type === HOLDING_TYPES.ALT) {
        // When the classification column is not enabled, we need to add the classification to the name
        const shouldAddClassification = holding.classification && !(typeof hasClassEnabled !== 'undefined' && hasClassEnabled);
        const i18nAltCustomDisclaimerPath = 'Disclaimers.AltCustomDisclaimer.Short.FundSuffix';
        return {
          [LOCALE.EN_CA]: shouldAddClassification
            ? ` ${holding.classification![LOCALE.EN_CA] as AltClass}${formatMessageWithLocale(i18nAltCustomDisclaimerPath, LOCALE.EN_CA)}`
            : '',
          [LOCALE.EN_US]: shouldAddClassification
            ? ` ${holding.classification![LOCALE.EN_US] as AltClass}${formatMessageWithLocale(i18nAltCustomDisclaimerPath, LOCALE.EN_US)}`
            : '',
          [LOCALE.FR_CA]: shouldAddClassification
            ? ` ${holding.classification![LOCALE.FR_CA] as AltClass}${formatMessageWithLocale(i18nAltCustomDisclaimerPath, LOCALE.FR_CA)}`
            : '',
        };
      }
      return generateLocalizedStringFromText(` (${holding.symbol})`);
    };

    const holdings = item.holdings.reduce((acc, holding) => {
      const existingHolding = acc.find(h => h.id === holding.id);
      if (
        existingHolding != null &&
        ((existingHolding.type !== HOLDING_TYPES.FUND && existingHolding.type !== HOLDING_TYPES.ALT) || existingHolding.symbol === holding.symbol)
      ) {
        existingHolding.weight += holding.weight;
        existingHolding.units = existingHolding.units != null && holding.units != null ? existingHolding.units + holding.units : undefined;
      } else {
        let { name } = holding;
        if (name != null && DataPoints.isSameItemWithDifferentSeries(item.holdings, holding)) {
          const extendedName = getLocalizedNameExtra(holding);
          name = {
            [LOCALE.EN_CA]: `${name[LOCALE.EN_CA]}${extendedName?.[LOCALE.EN_CA] ?? ''}`,
            [LOCALE.EN_US]: `${name[LOCALE.EN_US]}${extendedName?.[LOCALE.EN_US] ?? ''}`,
            [LOCALE.FR_CA]: `${name[LOCALE.FR_CA]}${extendedName?.[LOCALE.FR_CA] ?? ''}`,
          };
        }

        acc.push({
          ...holding,
          name,
        }); // shallow copy is enough as we only change weight/units/name
      }

      return acc;
    }, [] as ComparisonHolding[]);

    return {
      response: holdings,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.HOLDINGS_AS_AT, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets a dictionary of the asset allocations grouped by major groups
   * @param {ComparisonItem} item the proposal item to return allocations for
   */
  public static getAssetAllocationTotals(item: ComparisonItem): Dictionary<number> {
    const { allocations } = item;

    const defaultAllocations = {
      [ASSET_GROUPS.EQUITY]: 0,
      [ASSET_GROUPS.FIXED_INCOME]: 0,
      [ASSET_GROUPS.CASH]: 0,
      [ASSET_GROUPS.ALTERNATIVE_INVESTMENT]: 0,
      [ASSET_GROUPS.REAL_ASSETS]: 0,
      [ASSET_GROUPS.OTHER]: 0,
    };

    if (!allocations || !allocations.asset) {
      return defaultAllocations;
    }

    const assets = allocations.asset as Dictionary<number>;

    return Object.keys(assets).reduce((acc: Dictionary<number>, key: string) => {
      const value = assets[key];
      let allocKey;

      switch (key) {
        case PORTFOLIO_ASSET_ALLOCATION.CANADIAN_EQ:
        case PORTFOLIO_ASSET_ALLOCATION.US_EQ:
        case PORTFOLIO_ASSET_ALLOCATION.FOREIGN_EQ: {
          // Stop here - equity is already pre-calculated in a global field (see case `PORTFOLIO_ASSET_ALLOCATION.EQ`)
          return acc;
        }
        case PORTFOLIO_ASSET_ALLOCATION.EQ: {
          allocKey = ASSET_GROUPS.EQUITY;
          break;
        }
        case PORTFOLIO_ASSET_ALLOCATION.FIXED_INCOME:
          allocKey = ASSET_GROUPS.FIXED_INCOME;
          break;
        case PORTFOLIO_ASSET_ALLOCATION.CASH:
          allocKey = ASSET_GROUPS.CASH;
          break;
        case PORTFOLIO_ASSET_ALLOCATION.ALTERNATIVE_INVESTMENT:
          allocKey = ASSET_GROUPS.ALTERNATIVE_INVESTMENT;
          break;
        case PORTFOLIO_ASSET_ALLOCATION.REAL_ASSETS:
          allocKey = ASSET_GROUPS.REAL_ASSETS;
          break;
        default:
          allocKey = ASSET_GROUPS.OTHER;
          break;
      }

      acc[allocKey] = (acc[allocKey] || 0) + value;
      return acc;
    }, defaultAllocations);
  }

  /**
   * Gets the asset allocation group and their sub groups
   */
  public static getAssetAllocationGroups(): Dictionary<Dictionary<string>> {
    return {
      [ASSET_ALLOCATION_GROUPS.EQUITY]: {
        [PORTFOLIO_ASSET_ALLOCATION.CANADIAN_EQ]: PORTFOLIO_ASSET_ALLOCATION.CANADIAN_EQ,
        [PORTFOLIO_ASSET_ALLOCATION.US_EQ]: PORTFOLIO_ASSET_ALLOCATION.US_EQ,
        [PORTFOLIO_ASSET_ALLOCATION.FOREIGN_EQ]: PORTFOLIO_ASSET_ALLOCATION.FOREIGN_EQ,
      },
      [ASSET_ALLOCATION_GROUPS.FIXED_INCOME]: { [ASSET_ALLOCATION_GROUPS.FIXED_INCOME]: ASSET_ALLOCATION_GROUPS.FIXED_INCOME },
      [ASSET_ALLOCATION_GROUPS.OTHER]: { [ASSET_ALLOCATION_GROUPS.OTHER]: ASSET_ALLOCATION_GROUPS.OTHER },
    };
  }

  public static getAssetAllocationTotalsByCategory(item: ComparisonItem, category: string): DataPointResponse<number> {
    const allocationTotals = DataPoints.getAssetAllocationTotals(item);
    return {
      response: allocationTotals[category] || 0,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.ALLOCATIONS_AS_AT, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the asset allocation sub category by category key
   * @param {string} category the major category to retrieve categories for
   */
  public static getAssetAllocationSubCategories(category: string): Dictionary<string> {
    const groups = DataPoints.getAssetAllocationGroups();
    return groups[category];
  }

  /**
   * Gets the data keys for the sector allocation for an item
   * @param {ComparisonItem} item the item to retrieve the sector allocations for
   */
  public static getItemSectorAllocationsKeys(item: ComparisonItem): LocalizedString[] {
    const { allocations } = item;

    if (!allocations || !allocations.sector) {
      return [];
    }

    return allocations.sector.map(sectorAllocation => sectorAllocation.name);
  }

  /**
   * Gets the fixed sector allocation by key
   * @param {ComparisonItem} item the proposal item to get the value for
   * @param {string} sector the name of the sector to get the allocation weight for
   */
  public static getItemSectorAllocationByKey(item: ComparisonItem, sector: string): DataPointResponse<number> {
    const { allocations } = item;

    if (!allocations || !allocations.sector) {
      return { response: 0, disclaimerKeys: [] };
    }

    const match = allocations.sector.find(ca => ca.name[LOCALE.EN_CA] === sector);
    return {
      response: (match && match.weight) || 0,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.ALLOCATIONS_AS_AT, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the data keys for the fixed income sector allocation for an item
   * @param {ComparisonItem} item the item to retrieve the fixed income sector allocations for
   */
  public static getFixedIncomeSectorAllocationsKeys(item: ComparisonItem): LocalizedString[] {
    const { allocations } = item;

    if (!allocations || !allocations.fixedIncomeSector) {
      return [];
    }

    return allocations.fixedIncomeSector.map(sa => sa.name);
  }

  /**
   * Gets the fixed income maturity allocation by key
   * @param {ComparisonItem} item the proposal item to get the value for
   * @param {string} maturity the name of the maturity to get the allocation weight for
   */
  public static getCreditQualityAllocationByKey(item: ComparisonItem, creditQuality: string): DataPointResponse<number | undefined> {
    const { allocations } = item;
    if (!allocations || !allocations.creditQuality) {
      return { response: undefined, disclaimerKeys: [] };
    }

    let weight: number | undefined;

    if (creditQuality === CREDIT_QUALITY_CATEGORIES.BELOW_B) {
      const categories = allocations.creditQuality.filter(c => CREDIT_QUALITY_BELOW_B_CATEGORIES.includes(c.name));
      weight = categories.length > 0 ? categories.reduce((sum, val) => sum + val.weight, 0) : undefined;
    } else {
      const match = allocations.creditQuality.find(cq => cq.name === creditQuality);
      weight = match && match.weight;
    }

    return {
      response: weight,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.CREDIT_QUALITY_AS_AT, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the fixed income sector allocation by key
   * @param {ComparisonItem} item the proposal item to get the value for
   * @param {string} sector the name of the sector to get the allocation weight for
   */
  public static getFixedIncomeSectorAllocationByKey(item: ComparisonItem, sector: string): DataPointResponse<number> {
    const { allocations } = item;

    if (!allocations || !allocations.fixedIncomeSector) {
      return { response: 0, disclaimerKeys: [] };
    }

    const match = allocations.fixedIncomeSector.find(ca => ca.name[LOCALE.EN_CA] === sector);
    return {
      response: (match && match.weight) || 0,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.FIXED_INCOME_SECTOR, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the fixed income maturity allocation by key
   * @param {ComparisonItem} item the proposal item to get the value for
   * @param {string} maturity the name of the maturity to get the allocation weight for
   */
  public static getMaturityAllocationByKey(item: ComparisonItem, maturity: string): DataPointResponse<number | undefined> {
    const { allocations } = item;

    if (!allocations || !allocations.maturity) {
      return { response: 0, disclaimerKeys: [] };
    }

    const match = allocations.maturity.find(ca => ca.name[LOCALE.EN_CA] === maturity);
    const value: number | undefined = (match && match.weight) || undefined;
    return { response: value, disclaimerKeys: [PROPOSAL_DISCLAIMERS.MATURITY_AS_AT, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER] };
  }

  /**
   * Gets the fixed income region allocation by key
   * @param {ComparisonItem} item the proposal item to get the value for
   * @param {string} region the name of the region to get the allocation weight for
   */
  public static getFixedIncomeRegionAllocationByKey(item: ComparisonItem, region: string): DataPointResponse<number> {
    const { allocations } = item;

    if (!allocations || !allocations.fixedIncomeRegion) {
      return { response: 0, disclaimerKeys: [] };
    }

    const match = allocations.fixedIncomeRegion.find(regionAllocation => regionAllocation.name === region);
    return {
      response: (match && match.weight) || 0,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.FIXED_INCOME_REGION_AS_AT, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the fixed income region allocation by key
   * @param {ComparisonItem} item the proposal item to get the value for
   * @param {string} key the name of the region to get the allocation weight for
   * @param {Dictionary<string[]>} exclusionMap map of regions and the corresponding countries to extract out of them
   */
  public static getFixedIncomeRegionWithCountry(item: ComparisonItem, key: string, exclusionMap: Dictionary<string[]>): DataPointResponse<number> {
    const { allocations } = item;
    if (!allocations || !allocations.fixedIncomeRegion || !allocations.fixedIncomeCountry) {
      return { response: 0, disclaimerKeys: [] };
    }
    const copy = allocations.fixedIncomeRegion.map(far => ({ name: far.name, weight: far.weight }));
    const adjustedAllocations = copy.reduce((acc, current) => {
      const currentItem = { ...current };
      if (exclusionMap[currentItem.name]) {
        const countries = exclusionMap[currentItem.name];
        const countryAllocations = allocations.fixedIncomeCountry || [];
        const weights = countryAllocations.filter(ca => countries.includes(ca.name)).reduce((sum, c) => sum + c.weight, 0);
        currentItem.weight -= weights;
        for (const country of countries) {
          acc.push({ name: country, weight: DataPoints.getFixedIncomeCountryAllocationByKey(item, country).response });
        }
      }
      if (currentItem.weight > 0) {
        acc.push(currentItem);
      }
      return acc;
    }, [] as AllocationDetails[]);
    const match = adjustedAllocations.find(regionAllocation => regionAllocation.name === key);
    return {
      response: (match && match.weight) || 0,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.FIXED_INCOME_REGION_AS_AT, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the equity region allocation by key
   * @param {ComparisonItem} item the proposal item to get the value for
   * @param {string} key the name of the region to get the allocation weight for
   * @param {Dictionary<string[]>} exclusionMap map of regions and the corresponding countries to extract out of them
   */
  public static getEquityRegionWithCountry(item: ComparisonItem, key: string, exclusionMap: Dictionary<string[]>): DataPointResponse<number> {
    const { allocations } = item;
    if (!allocations || !allocations.equityRegion || !allocations.equityCountry) {
      return { response: 0, disclaimerKeys: [] };
    }
    const copy = allocations.equityRegion.map(er => ({ name: er.name, weight: er.weight }));
    const adjustedAllocations = copy.reduce((acc, current) => {
      const currentItem = { ...current };
      if (exclusionMap[currentItem.name]) {
        const countries = exclusionMap[currentItem.name];
        const countryAllocations = allocations.equityCountry || [];
        const weights = countryAllocations.filter(ca => countries.includes(ca.name)).reduce((sum, c) => sum + c.weight, 0);
        currentItem.weight -= weights;
        for (const country of countries) {
          acc.push({ name: country, weight: DataPoints.getEquityCountryAllocationByKey(item, country).response });
        }
      }
      if (currentItem.weight <= 0) {
        acc.push(currentItem);
      }
      return acc;
    }, [] as AllocationDetails[]);
    const match = adjustedAllocations.find(regionAllocation => regionAllocation.name === key);
    return {
      response: (match && match.weight) || 0,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.ALLOCATIONS_AS_AT, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the equity region allocation by key
   * @param {ComparisonItem} item the proposal item to get the value for
   * @param {string} region the name of the region to get the allocation weight for
   */
  public static getEquityRegionAllocationByKey(item: ComparisonItem, region: string): DataPointResponse<number> {
    const { allocations } = item;

    if (!allocations || !allocations.equityRegion) {
      return { response: 0, disclaimerKeys: [] };
    }
    const match = allocations.equityRegion.find(regionAllocation => regionAllocation.name === region);
    return {
      response: (match && match.weight) || 0,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.ALLOCATIONS_AS_AT, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the equity country allocations
   * @param {ComparisonItem} item the proposal item to get the value for
   */
  public static getEquityCountryAllocations(item: ComparisonItem): string[] {
    const { allocations } = item;

    if (allocations === undefined || allocations.equityCountry === undefined) {
      return [];
    }

    return allocations.equityCountry.map(region => region.name);
  }

  /**
   * Gets the data keys for the fixed income region allocations for an item
   * @param {ComparisonItem} item the item to retrieve the fixed income region allocations for
   */
  public static getFixedIncomeRegionAllocationsKeys(item: ComparisonItem): string[] {
    const { allocations } = item;

    if (!allocations || !allocations.fixedIncomeRegion) {
      return [];
    }

    return allocations.fixedIncomeRegion.map(region => region.name);
  }

  /**
   * Gets the data keys for the equity region allocations for an item
   * @param {ComparisonItem} item the item to retrieve the fixed income region allocations for
   */
  public static getEquityRegionAllocationsKeys(item: ComparisonItem): string[] {
    const { allocations } = item;

    if (!allocations || !allocations.equityRegion) {
      return [];
    }

    return allocations.equityRegion.map(region => region.name);
  }

  /**
   * Gets the data keys for the fixed income maturity allocations for an item
   * @param {ComparisonItem} item the item to retrieve the fixed income maturity allocations for
   */
  public static getMaturityAllocationsKeys(item: ComparisonItem): LocalizedString[] {
    const { allocations } = item;

    if (!allocations || !allocations.maturity) {
      return [];
    }

    return allocations.maturity.map(sa => sa.name);
  }

  /**
   * Gets the data keys for the equity capitalization allocations for an item
   * @param {ComparisonItem} item the item to retrieve the equity capitalization keys for
   */
  public static getEquityCapitalizationKeys(item: ComparisonItem): LocalizedString[] {
    const { investmentInfo } = item;

    if (!investmentInfo || !investmentInfo.capitalization) {
      return [];
    }

    return investmentInfo.capitalization.map(capitalizationAllocation => capitalizationAllocation.name);
  }

  /**
   * Gets the equity capitalization allocation by key (Growth or Value or Blend)
   * @param {ComparisonItem} item the item to retrieve the equity capitalization for
   * @param {string} styleKey the name of the capitalization to get the allocation weight for
   */
  public static getEquityCapitalizationByKey(item: ComparisonItem, capitalizationKey: string): DataPointResponse<number> {
    const { investmentInfo } = item;

    if (!investmentInfo || !investmentInfo.capitalization) {
      return { response: 0, disclaimerKeys: [] };
    }
    const match = investmentInfo.capitalization.find(ca => ca.name[LOCALE.EN_CA] === capitalizationKey);
    return {
      response: (match && match.weight) || 0,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.EQUITY_STYLE, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the data keys for the equity style allocations for an item
   * @param {ComparisonItem} item the item to retrieve the equity style keys for
   */
  public static getEquityStyleKeys(item: ComparisonItem): LocalizedString[] {
    const { investmentInfo } = item;

    if (!investmentInfo || !investmentInfo.style) {
      return [];
    }

    return investmentInfo.style.map(styleAllocation => styleAllocation.name);
  }

  /**
   * Gets the equity style allocation by key (Growth or Value or Blend)
   * @param {ComparisonItem} item the item to retrieve the equity style for
   * @param {string} styleKey the name of the style to get the allocation weight for
   */
  public static getEquityStyleByKey(item: ComparisonItem, styleKey: string): DataPointResponse<number> {
    const { investmentInfo } = item;

    if (!investmentInfo || !investmentInfo.style) {
      return { response: 0, disclaimerKeys: [] };
    }
    const match = investmentInfo.style.find(ca => ca.name[LOCALE.EN_CA] === styleKey);
    return {
      response: (match && match.weight) || 0,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.EQUITY_STYLE],
    };
  }

  /**
   * Gets the data keys for the duration class allocations for an item
   * @param {ComparisonItem} item the item to retrieve the duration class keys for
   */
  public static getDurationClassKeys(item: ComparisonItem): LocalizedString[] {
    const { investmentInfo } = item;

    if (!investmentInfo || !investmentInfo.durationClass) {
      return [];
    }

    return investmentInfo.durationClass.map(dc => dc.name);
  }

  /**
   * Gets the equity style allocation by key (High, Low, Med)
   * @param {ComparisonItem} item the item to retrieve the duration class for
   * @param {string} durationClassKey the name of the duration class to get the allocation weight for
   */
  public static getDurationClassByKey(item: ComparisonItem, durationClassKey: string): DataPointResponse<number> {
    const { investmentInfo } = item;

    if (!investmentInfo || !investmentInfo.durationClass) {
      return { response: 0, disclaimerKeys: [] };
    }

    const match = investmentInfo.durationClass.find(ca => ca.name[LOCALE.EN_CA] === durationClassKey);
    return {
      response: (match && match.weight) || 0,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.FIXED_INCOME_STYLE, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the data keys for the credit rating class allocations for an item
   * @param {ComparisonItem} item the item to retrieve the credit rating class keys for
   */
  public static getCreditRatingClassKeys(item: ComparisonItem): LocalizedString[] {
    const { investmentInfo } = item;

    if (!investmentInfo || !investmentInfo.creditRatingClass) {
      return [];
    }

    return investmentInfo.creditRatingClass.map(creditClass => creditClass.name);
  }

  /**
   * Gets the equity style allocation by key (High, Low, Med)
   * @param {ComparisonItem} item the item to retrieve the credit rating value for
   * @param {string} creditClassKey the name of the credit class to get the allocation weight for
   */
  public static getCreditRatingClassByKey(item: ComparisonItem, creditClassKey: string): DataPointResponse<number> {
    const { investmentInfo } = item;

    if (!investmentInfo || !investmentInfo.creditRatingClass) {
      return { response: 0, disclaimerKeys: [] };
    }

    const match = investmentInfo.creditRatingClass.find(creditClass => creditClass.name[LOCALE.EN_CA] === creditClassKey);
    return {
      response: (match && match.weight) || 0,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.FIXED_INCOME_STYLE, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the equity metrics keys (returnOnEquity, pbRatio, peRatio, distributionYield)
   * @param {ComparisonItem} item the item to retrieve the keys with
   */
  public static getEquityMetricsKeys(item: ComparisonItem): string[] {
    const metrics = DataPoints.getEquityMetrics(item);

    return Object.keys(metrics);
  }

  /**
   * Gets the equity metrics for an item
   * @param {ComparisonItem} item the item to retrieve the equity metrics for
   */
  public static getEquityMetrics(item: ComparisonItem): DataPointResponse<Dictionary<number | undefined>> {
    const { investmentInfo, characteristics } = item;

    if (!investmentInfo && !characteristics) {
      return { response: {}, disclaimerKeys: [] };
    }

    return {
      response: {
        returnOnEquity: characteristics && characteristics.returnOnEquity,
        pbRatio: investmentInfo && investmentInfo.pbRatio,
        peRatio: investmentInfo && investmentInfo.peRatio,
        distributionYield: investmentInfo && investmentInfo.distributionYield,
      },
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.RETURN_ON_EQUITY, PROPOSAL_DISCLAIMERS.EQUITY_OTHER_METRICS, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets an equity metric by key
   * @param {ComparisonItem} item the item to retrieve the equity metric value for
   * @param {string} key the name of the metric to get the value for
   */
  public static getItemEquityMetricByKey(item: ComparisonItem, key: string): DataPointResponse<number | undefined> {
    const metrics = DataPoints.getEquityMetrics(item);
    return {
      response: metrics.response[key],
      disclaimerKeys: [
        PROPOSAL_DISCLAIMERS.ALLOCATIONS_AS_AT,
        PROPOSAL_DISCLAIMERS.RETURN_ON_EQUITY,
        PROPOSAL_DISCLAIMERS.EQUITY_OTHER_METRICS,
        PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER,
      ],
    };
  }

  /**
   * Gets the fixed income metrics for an item
   * @param {ComparisonItem} item the item to retrieve the equity metrics for
   */
  public static getIncomeMetrics(item: ComparisonItem): DataPointResponse<Dictionary<number | string | Date | undefined>> {
    const response: Dictionary<number | string | Date | undefined> = {
      averageCoupon: item.characteristics && item.characteristics.averageCoupon,
      averageQuality: item.characteristics && item.characteristics.averageQuality,
      yieldToMaturity: item.characteristics && item.characteristics.yieldToMaturity?.value,
      effectiveMaturity: item.characteristics && item.characteristics.effectiveMaturity,
      effectiveDuration: item.investmentInfo && item.investmentInfo.effectiveDuration,
      modifiedDuration: item.investmentInfo && item.investmentInfo.modifiedDuration,
      faceValue: item.characteristics && item.characteristics.faceValue,
      maturityDate: item.characteristics && item.characteristics.maturityDate,
      frequency: item.characteristics && item.characteristics.frequency,
      frequencyUnit: item.characteristics && item.characteristics.frequencyUnit,
      couponDates: getCouponDates(item.characteristics && item.characteristics.frequency, item.characteristics && item.characteristics.frequencyUnit),
    };

    const disclaimerKeys: Array<string> = [
      PROPOSAL_DISCLAIMERS.MODIFIED_DURATION,
      PROPOSAL_DISCLAIMERS.YIELD_TO_MATURITY,
      PROPOSAL_DISCLAIMERS.OTHER_FIXED_INCOME_METRICS,
      PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER,
    ];

    return { response, disclaimerKeys };
  }

  /**
   * Gets the country allocation keys for an item
   * @param {ComparisonItem} item the item to get the country allocation keys for
   */
  public static getItemCountryAllocationsKeys(item: ComparisonItem): string[] {
    const { allocations } = item;

    if (!allocations || !allocations.country) {
      return [];
    }

    return allocations.country.map(countryAllocation => countryAllocation.name);
  }

  /**
   * Gets the detailed holding allocation keys for an item
   * @param {ComparisonItem} item the item to get the detailed holding allocation keys for
   */
  public static getItemDetailedHoldingAllocationsKeys(item: ComparisonItem): LocalizedString[] {
    const { allocations } = item;

    if (!allocations || !allocations.detailedAsset) {
      return [];
    }

    return allocations.detailedAsset.map(asset => asset.name);
  }

  /**
   * Gets all unique sector allocation keys for a list of items
   * @param {ComparisonItem[]} items the items to get the unique sector keys for
   */
  public static getAllSectorAllocationsKeys(items: ComparisonItem[]): LocalizedString[] {
    const result = new Set<LocalizedString>();

    for (const item of items) {
      const keys = DataPoints.getItemSectorAllocationsKeys(item);
      keys.forEach(key => result.add(key));
    }

    return Array.from(result);
  }

  /**
   * Gets all unique country allocation keys for a list of items
   * @param {ComparisonItem[]} items the items to get the unique country keys for
   */
  public static getAllCountryAllocationsKeys(items: ComparisonItem[]): string[] {
    const result = new Set<string>();

    for (const item of items) {
      const keys = DataPoints.getItemCountryAllocationsKeys(item);
      keys.forEach(key => result.add(key));
    }

    return Array.from(result);
  }

  /**
   * Gets all unique detailed holdings keys for a list of items
   * @param {ComparisonItem[]} items the items to get the unique detailed holdings keys for
   */
  public static getItemsDetailedHoldings(items: ComparisonItem[]): LocalizedString[] {
    const result = new Set<LocalizedString>();

    for (const item of items) {
      const keys = DataPoints.getItemDetailedHoldingAllocationsKeys(item);
      keys.forEach(key => result.add(key));
    }

    return Array.from(result);
  }

  /**
   * Gets etailed holdings for an item
   * @param {ComparisonItem} item the item to get detailed holdings for
   */
  public static getDetailedHoldings(item: ComparisonItem): DataPointResponse<AllocationDetails[]> {
    if (item.detailedHoldings !== undefined) {
      return { response: item.detailedHoldings.list, disclaimerKeys: [PROPOSAL_DISCLAIMERS.HOLDINGS_AS_AT, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER] };
    }

    return { response: [], disclaimerKeys: [] };
  }

  /**
   * Gets the detailed holdings for an item by key (holding name)
   * @param {ComparisonItem} item the proposal item to get the calendar year return for
   * @param {string} holdingName the name of the holding to get the allocation weight for
   */
  public static getItemDetailedHoldingByKey(item: ComparisonItem, holdingName: string): DataPointResponse<number> {
    const { allocations } = item;

    if (!allocations || !allocations.detailedAsset) {
      return { response: 0, disclaimerKeys: [] };
    }

    const value = allocations.detailedAsset.find(alloc => alloc.name[LOCALE.EN_CA] === holdingName);
    return {
      response: (value && value.weight) || 0,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.HOLDINGS_AS_AT, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the calendar year returns for an item
   * @param item the proposal item to get the calendar year return for
   */
  public static getCalendarYearReturns(
    item: ComparisonItem,
    useBaseCurrency: boolean = false,
  ): DataPointResponse<Dictionary<ResponseHasDisclaimer<number | undefined>>> {
    const calendarYear = useBaseCurrency && item.performanceBase ? item.performanceBase.calendarYear : item.performance.calendarYear;

    if (!calendarYear) {
      return {
        response: {},
        disclaimerKeys: [],
      };
    }

    const values: Dictionary<ResponseHasDisclaimer<number | undefined>> = {};

    const keys = Object.keys(calendarYear);
    const fields =
      item.disclaimers &&
      item.disclaimers[DISCLAIMER_KEYS.SHORT_HOLDINGS] &&
      item.disclaimers[DISCLAIMER_KEYS.SHORT_HOLDINGS].data &&
      item.disclaimers[DISCLAIMER_KEYS.SHORT_HOLDINGS].data.fields;
    for (const key of keys) {
      const stringKey = `performance_calendarYear_${key}`;
      let hasDisclaimer: boolean | undefined;
      if (fields != null) {
        for (const field of fields) {
          if (field.field === stringKey && field.holdings.length > 0) {
            hasDisclaimer = true;
            break;
          }
        }
      }
      values[key] = { value: calendarYear[key], hasDisclaimer };
    }

    return {
      response: values,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.NET_PERFORMANCE, PROPOSAL_DISCLAIMERS.SHORT_HISTORY_PORTFOLIOS, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the calendar year returns for an item by year
   * @param item the proposal item to get the calendar year return for
   * @param year the year to get the return for
   */
  public static getCalendarYearReturnsByYear(item: ComparisonItem, year: number): DataPointResponse<ResponseHasDisclaimer<number | undefined>> {
    const returns = DataPoints.getCalendarYearReturns(item);
    return {
      response: returns.response[year.toString()],
      disclaimerKeys: returns.disclaimerKeys,
    };
  }

  /**
   * Gets the percent growths returns for a value
   * @param item the proposal item to get the percent growth return for
   */
  public static getPercentGrowths(
    item: ComparisonItem,
    useBaseCurrency: boolean,
    forcedInceptionDateOnBenchmarks?: number,
  ): DataPointResponse<PercentGrowthHasDisclaimer> {
    let percentGrowth = useBaseCurrency && item.performanceBase ? item.performanceBase.percentGrowth : item.performance.percentGrowth;

    if (percentGrowth != null) {
      const monthly = useBaseCurrency && item.performanceBase ? item.performanceBase.rolling?.monthly : item.performance.rolling?.monthly;
      if (monthly != null) {
        if (item.type === ProposalItemType.Benchmark && forcedInceptionDateOnBenchmarks != null) {
          const returns = calculateTrailingReturns(trimRollingData(monthly, forcedInceptionDateOnBenchmarks, Date.now(), false));
          percentGrowth = {
            ...percentGrowth,
            INC: returns.INC,
          };
        }
      }
    }

    const values: PercentGrowthHasDisclaimer = Object.keys(TIME_PERIODS).reduce((acc, key) => {
      acc[key as GrowthTypes] = { value: null };
      return acc;
    }, {} as PercentGrowthHasDisclaimer);

    if (percentGrowth == null) {
      return {
        response: values,
        disclaimerKeys: [],
      };
    }

    const disclaimerKeys: Array<string> = [
      PROPOSAL_DISCLAIMERS.NET_PERFORMANCE,
      PROPOSAL_DISCLAIMERS.PERFORMANCE_PERIOD,
      PROPOSAL_DISCLAIMERS.SHORT_HISTORY_PORTFOLIOS,
      PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER,
    ];

    const keys = Object.keys(percentGrowth);
    const fields =
      item.disclaimers &&
      item.disclaimers[DISCLAIMER_KEYS.SHORT_HOLDINGS] &&
      item.disclaimers[DISCLAIMER_KEYS.SHORT_HOLDINGS].data &&
      item.disclaimers[DISCLAIMER_KEYS.SHORT_HOLDINGS].data.fields;
    for (const key of keys) {
      const stringKey = `performance_percentGrowth_${key}`;
      let hasDisclaimer: boolean | undefined;
      if (fields != null) {
        for (const field of fields) {
          if (field.field === stringKey && field.holdings.length > 0) {
            hasDisclaimer = true;
            break;
          }
        }
      }
      values[key as GrowthTypes] = { value: percentGrowth[key as GrowthTypes], hasDisclaimer };
    }

    return { response: values, disclaimerKeys };
  }

  /**
   * Gets the time frame keys for the percent growths (1D,6M,1Y,3Y etc.)
   */
  public static getPercentGrowthKeys(): string[] {
    return Object.keys(DEFAULT_GROWTH_VALUES);
  }

  /**
   * Gets the percent growth for an item by period
   * @param item the proposal item to get the percent growth for
   * @param period the period to get the return for
   */
  public static getPercentGrowthByPeriod(
    item: ComparisonItem,
    period: GrowthTypes,
    useBaseCurrency: boolean,
  ): DataPointResponse<ResponseHasDisclaimer<number | null>> {
    const growth: DataPointResponse<PercentGrowthHasDisclaimer> = DataPoints.getPercentGrowths(item, useBaseCurrency);
    return { response: growth.response[period], disclaimerKeys: growth.disclaimerKeys };
  }

  /**
   * Gets the risk metrics for an item
   * @param {ComparisonItem} item the item to get the risk metrics for
   */
  public static getRiskMetrics(
    item: ComparisonItem,
    useBaseCurrency: boolean,
    forcedStandardDeviationInceptionDateOnBenchmarks?: number,
  ): DataPointResponse<Dictionary<RiskMetricHasDisclaimer>> {
    const performance = useBaseCurrency && item.performanceBase ? item.performanceBase : item.performance;

    if (item.type === ProposalItemType.Benchmark && performance.standardDeviation && forcedStandardDeviationInceptionDateOnBenchmarks != null) {
      const monthly = useBaseCurrency && item.performanceBase ? item.performanceBase.rolling?.monthly : item.performance.rolling?.monthly;
      if (monthly != null) {
        const returns = trimRollingData(monthly, forcedStandardDeviationInceptionDateOnBenchmarks, Date.now()).map(p => p[1]);
        const { standardDeviation } = getVarianceMetrics(returns);
        performance.standardDeviation.INC = standardDeviation ? standardDeviation * Math.sqrt(12) : null;
      }
    }

    const values: RiskMetricHasDisclaimer = Object.keys(TIME_PERIODS).reduce((acc, key) => {
      acc[key as RiskPeriods] = { value: null };
      return acc;
    }, {} as RiskMetricHasDisclaimer);

    const disclaimerKeys: Array<string> = [
      PROPOSAL_DISCLAIMERS.RISK_PERIOD,
      PROPOSAL_DISCLAIMERS.SHORT_HISTORY_PORTFOLIOS,
      PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER,
    ];

    const fields =
      item.disclaimers &&
      item.disclaimers[DISCLAIMER_KEYS.SHORT_HOLDINGS] &&
      item.disclaimers[DISCLAIMER_KEYS.SHORT_HOLDINGS].data &&
      item.disclaimers[DISCLAIMER_KEYS.SHORT_HOLDINGS].data.fields;

    const getResponse = (metric: RiskMetric | undefined): RiskMetricHasDisclaimer => {
      const localValues = deepClone(values);
      if (metric != null) {
        const keys = Object.keys(metric);
        for (const key of keys) {
          const stringKey = `performance_percentGrowth_${key}`;
          let hasDisclaimer = false;
          if (fields != null) {
            for (const field of fields) {
              if (field.field === stringKey && field.holdings.length > 0) {
                hasDisclaimer = true;
                break;
              }
            }
          }
          localValues[key as RiskPeriods] = { value: metric[key as RiskPeriods], hasDisclaimer };
        }
      }

      return localValues;
    };

    const riskMetrics: Dictionary<RiskMetricHasDisclaimer> = {
      rSquared: getResponse(performance.rSquared),
      infoRatio: getResponse(performance.infoRatio),
      standardDeviation: getResponse(performance.standardDeviation),
      beta: getResponse(performance.beta),
      downside: getResponse(performance.downside),
      upside: getResponse(performance.upside),
      sortino: getResponse(performance.sortino),
      maximumDrawdown: getResponse(performance.maximumDrawdown),
      correlationCoefficient: getResponse(performance.correlationCoefficient),
      sharpeRatio: getResponse(performance.sharpeRatio),
      trackingError: getResponse(performance.trackingError),
      alpha: getResponse(performance.alpha),
    };

    return { response: riskMetrics, disclaimerKeys };
  }

  /**
   * Gets the risk metrics for an item
   * @param {ComparisonItem} item the item to get the risk metrics for
   */
  public static getRiskMetricsKeys(item: ComparisonItem, useBaseCurrency: boolean): string[] {
    const riskMetrics = DataPoints.getRiskMetrics(item, useBaseCurrency);
    return Object.keys(riskMetrics.response);
  }

  /**
   * Gets a risk metric for an item by key
   * @param {ComparisonItem} item the item to get the risk metric for
   */
  public static getRiskMetricsByKey(item: ComparisonItem, key: string, useBaseCurrency: boolean): DataPointResponse<RiskMetricHasDisclaimer | undefined> {
    const metricResponse = DataPoints.getRiskMetrics(item, useBaseCurrency);
    const metric = metricResponse.response ? metricResponse.response[key] : undefined;
    return { response: metric, disclaimerKeys: metricResponse.disclaimerKeys };
  }

  /**
   * Gets a risk metric for an item by key
   * @param {ComparisonItem} item the item to get the risk metric for
   * @param {string} key the name of the risk metric to get
   * @param {RiskPeriods} period the period to get the risk metric value for
   */
  public static getRiskMetricsByPeriod(
    item: ComparisonItem,
    key: string,
    period: RiskPeriods,
    useBaseCurrency: boolean,
  ): DataPointResponse<ResponseHasDisclaimer<number | null> | undefined> {
    const metricResponse: DataPointResponse<RiskMetricHasDisclaimer | undefined> = DataPoints.getRiskMetricsByKey(item, key, useBaseCurrency);
    const metric: ResponseHasDisclaimer<number | null> | undefined = metricResponse.response ? metricResponse.response[period] : undefined;
    return { response: metric, disclaimerKeys: metricResponse.disclaimerKeys };
  }

  /**
   * Gets the correlation matrix for an item by period
   * @param {ComparisonItem} item the item to get the matrix for
   * @param {RiskPeriods} period the period to get the matrix value for
   */
  public static getCorrelationMatrixByPeriod(
    item: ComparisonItem,
    period: RiskPeriods,
    isReadOnly: boolean,
    options: CorrelationMatrixOptions = DEFAULT_CORRELATION_OPTIONS,
  ): DataPointResponse<CorrelationData[] | undefined> {
    const { useBaseCurrency } = options;
    const performance = useBaseCurrency && item.performanceBase ? item.performanceBase : item.performance;
    return {
      response:
        isReadOnly && performance.correlation?.[period] && performance.correlation?.[period].length
          ? performance.correlation[period]
          : getCorrelationMatrix(item, period, options),
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.RISK_PERIOD, PROPOSAL_DISCLAIMERS.SHORT_HISTORY_PORTFOLIOS, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the correlation matrix between all items of a comparison by period
   * @param {ComparisonItem} item the item to get the matrix for
   * @param {RiskPeriods} period the period to get the matrix value for
   */
  public static getItemsCorrelationMatrixByPeriod(
    itemsCorrelation: CorrelationMetric | undefined,
    period: RiskPeriods,
  ): DataPointResponse<CorrelationData[] | undefined> {
    const correlation = itemsCorrelation && itemsCorrelation[period];

    if (!correlation) {
      return { response: undefined, disclaimerKeys: [] };
    }

    return {
      response: correlation,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.SHORT_HISTORY_PORTFOLIOS, PROPOSAL_DISCLAIMERS.RISK_PERIOD, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Get time periods (used for PercentGrowth , risk metrics)
   */
  public static getTimePeriods() {
    const keys = Object.keys(TIME_PERIODS);
    return keys;
  }

  /**
   * Gets the target asset allocations from a risk profile
   * @param {RiskProfileItem} riskProfile the risk profile to get the allocations for
   */
  public static getTargetAllocations = (riskProfile: RiskProfileItem): Dictionary<MinMax> => {
    // This has been adjusted to avoid refactoring the existing templates as we changed the KYC to use a rule-based system.
    // The templates that are still using this would always only have an asset allocation rule - if an enterprise
    // has a KYC that uses more than that, we should adjust their templates to use the CUSTOM_RISK_PROFILE proposal module.
    const rule = (riskProfile.rules as RiskProfileRule[]).find(rule => rule.ruleType === RiskProfileRuleType.AssetAllocation) as
      | RiskProfileAssetAllocationRule
      | undefined;
    if (!rule) {
      return {};
    }

    const cash = rule && rule.value.find(val => val.assetType === InvestmentAssetType.Cash);
    const equity = rule && rule.value.find(val => val.assetType === InvestmentAssetType.Equity);
    const fixedIncome = rule && rule.value.find(val => val.assetType === InvestmentAssetType.FixedIncome);

    const result = {
      [ASSET_ALLOCATION_GROUPS.EQUITY]: {
        min: equity ? equity.min : 0,
        max: equity ? equity.max : 100,
      },
      [ASSET_ALLOCATION_GROUPS.FIXED_INCOME]: {
        min: fixedIncome ? fixedIncome.min : 0,
        max: fixedIncome ? fixedIncome.max : 100,
      },
    };

    if (cash) {
      result[ASSET_ALLOCATION_GROUPS.CASH] = {
        min: cash.min,
        max: cash.max,
      };
    }

    return result;
  };

  /**
   * Gets the target asset allocation from a risk profile by key
   * @param {RiskProfileItem} riskProfile the risk profile to get the allocation for
   * @param {string} key the key of the allocation to retrieve
   */
  public static getTargetAllocationByKey = (riskProfile: RiskProfileItem, key: string): MinMax | undefined => {
    const targets = DataPoints.getTargetAllocations(riskProfile);
    return targets[key];
  };

  /**
   * Calculates the difference between a number and a set of min/max values.
   * @param {number} value Value to calculate the difference from.
   * @param {number} min Min value of the target.
   * @param {number} max Max value of the target.
   */
  public static getTargetAdjustment(value: number, min: number = 0, max: number = 100): number {
    if (value < min) {
      return min - value;
    }

    if (value > max) {
      return max - value;
    }

    return 0;
  }

  /**
   * Gets the adjustment for the asset allocation vs a target allocation from a risk profile
   * @param {ComparisonItem} item the proposal item to get the target allocation adjustment for
   * @param {RiskProfileItem} riskProfile the risk profile to compare against
   * @param {string} key the key of the allocation to get the adjustment for
   */
  public static getAdjustmentForTargetAllocation(item: ComparisonItem, riskProfile: RiskProfileItem, key: string): number {
    if (key === 'OTHER') {
      return 0;
    }

    const totalAllocations = DataPoints.getAssetAllocationTotals(item);
    const target = DataPoints.getTargetAllocationByKey(riskProfile, key);
    if (!target) {
      return 0;
    }

    let value = totalAllocations[key];

    const rule = (riskProfile.rules as RiskProfileRule[]).find(rule => rule.ruleType === RiskProfileRuleType.AssetAllocation) as
      | RiskProfileAssetAllocationRule
      | undefined;
    if (!rule) {
      return 0;
    }

    // Include CASH as fixed income for target allocations, unless there's a rule specific to cash.
    if (key === 'FIXED_INCOME' && !rule.value.some(val => val.assetType === InvestmentAssetType.Cash)) {
      value += totalAllocations.CASH;
    }

    if (!target) {
      return -value;
    }

    return DataPoints.getTargetAdjustment(value, target.min, target.max);
  }

  /**
   * Gets the best worst performance for a period and a specified interval
   * ***Example: (3Year Period split into 3Months intervals)***
   * @param {ComparisonItem} item the proposal item to get the best worst performance for
   * @param {TimePeriods} period the period to get the best worst performance for
   * @param {TimePeriods} interval the interval to get the best worst performance for
   */
  public static getBestWorstPerformance(
    item: ComparisonItem,
    period: TimePeriods,
    interval: TimePeriods,
    useBaseCurrency: boolean,
  ): DataPointResponse<BestWorstMetric> {
    const bestWorst = useBaseCurrency && item.performanceBase ? item.performanceBase.bestWorst : item.performance.bestWorst;
    if (!bestWorst) {
      return {
        response: {
          best: { value: null, start: null, end: null },
          worst: { value: null, start: null, end: null },
        },
        disclaimerKeys: [],
      };
    }

    const periodValues = bestWorst[period];
    return {
      response: periodValues[interval],
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.NET_PERFORMANCE, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the best worst performance for a period and a specified interval
   * ***Example: (3Year Period split into 3Months intervals)***
   * @param {ComparisonItem} item the proposal item to get the target allocation adjustment for
   * @param {TimePeriods} period the period to get the up down months for
   */
  public static getUpDownMonthsByPeriod(item: ComparisonItem, period: RiskPeriods, useBaseCurrency: boolean): DataPointResponse<{ up: number; down: number }> {
    const upDownMonths = useBaseCurrency && item.performanceBase ? item.performanceBase.upDownMonths : item.performance.upDownMonths;

    if (!upDownMonths) {
      return { response: { up: 0, down: 0 }, disclaimerKeys: [] };
    }

    return {
      response: upDownMonths[period as RiskPeriods],
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.UP_DOWN_MONTHS],
    };
  }

  /**
   * Gets the fund MER
   * @param {ComparisonItem} item the proposal item to get the mer for
   */
  public static getMer(item: ComparisonItem): DataPointResponse<MERBreakdown> {
    const { mer, discretionaryMer, tax } = item;

    let totalMER: number | undefined;
    if (mer != null) {
      totalMER = mer;
    }

    if (discretionaryMer != null) {
      if (totalMER != null) {
        totalMER += discretionaryMer;
      } else {
        totalMER = discretionaryMer;
      }

      if (tax != null) {
        totalMER += tax;
      }
    }

    const result = {
      mer,
      fee: discretionaryMer,
      tax,
      total: totalMER,
    } as MERBreakdown;

    return { response: result, disclaimerKeys: mer ? [PROPOSAL_DISCLAIMERS.FUNDATA_MERS] : [] };
  }

  /**
   * Gets the fund minimum investment
   * @param {ComparisonItem} item the proposal item to get the min investment for
   */
  public static getMinInvestmentInfo(item: ComparisonItem): DataPointResponse<MinInvestmentInfo | undefined> {
    const { minInvestments } = item;
    return { response: minInvestments, disclaimerKeys: [] };
  }

  /**
   * Gets the fund risk rating
   * @param {ComparisonItem} item The proposal item to get the risk rating for
   */
  public static getRiskRating(item: ComparisonItem): DataPointResponse<RiskRating | undefined> {
    const { riskRating } = item;

    const disclaimerKeys: string[] = [];

    if (item.disclaimers?.[DISCLAIMER_KEYS.CUSTOM_PROVIDER_RISK_RATING]) {
      disclaimerKeys.push(DISCLAIMER_KEYS.CUSTOM_PROVIDER_RISK_RATING);
    }

    return { response: riskRating, disclaimerKeys };
  }

  /**
   * Gets the fund Hypothetical Return
   * @param {ChartItem} item chart item we want to get series data for
   */
  public static getHypotheticalReturnSeries(
    item: ChartItem,
    rollingInterval: RollingIntervals,
    capital: number,
    useBaseCurrency: boolean,
  ): DataPointResponse<Array<LineSeriesDataPoint>> {
    let data: [Timestamp, number][] = [];
    if (useBaseCurrency && item.performanceBase && item.performanceBase.rolling && item.performanceBase.rolling[rollingInterval]) {
      data = item.performanceBase.rolling[rollingInterval];
    } else if (item.performance.rolling && item.performance.rolling[rollingInterval]) {
      data = item.performance.rolling[rollingInterval];
    }

    return DataPoints.getHypotheticalReturnSeriesForRollings(data, capital);
  }

  public static getHistoricalScenarioSeriesForRollings(data: [Timestamp, number][], capital: number) {
    let acc = capital;
    if (data.length === 0) {
      return { response: [], disclaimerKeys: [] };
    }

    const series: LineSeriesDataPoint[] = data.map((timestampValuePair, index) => {
      if (index === 0) {
        return [timestampValuePair[0], parseFloat(acc.toFixed(2))] as LineSeriesDataPoint;
      }

      acc += acc * (timestampValuePair[1] / 100);
      return [timestampValuePair[0], parseFloat(acc.toFixed(2))] as LineSeriesDataPoint;
    });

    return {
      response: series,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.NET_PERFORMANCE, PROPOSAL_DISCLAIMERS.HISTORICAL_SCENARIO_RETURN],
    };
  }

  public static getHypotheticalReturnSeriesForRollings(data: [Timestamp, number][], capital: number) {
    let acc = capital;
    if (data.length === 0) {
      return { response: [], disclaimerKeys: [] };
    }

    const series: LineSeriesDataPoint[] = data.map((timestampValuePair, index) => {
      if (index === 0) {
        return [timestampValuePair[0], parseFloat(acc.toFixed(2))] as LineSeriesDataPoint;
      }

      acc += acc * (timestampValuePair[1] / 100);
      return [timestampValuePair[0], parseFloat(acc.toFixed(2))] as LineSeriesDataPoint;
    });

    return {
      response: series,
      disclaimerKeys: [
        PROPOSAL_DISCLAIMERS.HYPOTHETICAL_RETURN,
        PROPOSAL_DISCLAIMERS.SHORT_HISTORY_PORTFOLIOS,
        PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER,
      ],
    };
  }

  /**
   * Gets the fund Drawdown Analysis
   * @param {ComparisonItem} item the proposal item to get the mer for
   */
  public static getDrawdownAnalysisSeries(
    item: ChartItem,
    rollingInterval: RollingIntervals,
    useBaseCurrency: boolean,
  ): DataPointResponse<Array<LineSeriesDataPoint>> {
    const performance = useBaseCurrency && item.performanceBase ? item.performanceBase : item.performance;

    if (performance.rolling && performance.rolling[rollingInterval]) {
      const data: DrawdownData = calculateDrawdownData(performance.rolling[rollingInterval]);

      return {
        response: data.drawdowns,
        disclaimerKeys: [
          PROPOSAL_DISCLAIMERS.DRAWDOWN_ANALYSIS,
          PROPOSAL_DISCLAIMERS.SHORT_HISTORY_PORTFOLIOS,
          PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER,
        ],
      };
    }

    return { response: [], disclaimerKeys: [] };
  }

  /**
   * Gets the item's Drawdown data (drawdown data and the drawdown details)
   * @param {ComparisonItem} item the proposal item to get the mer for
   */
  public static getDrawdownDetailsData(
    item: ChartItem,
    rollingInterval: RollingIntervals,
    startDate: Date | null,
    endDate: Date,
    useBaseCurrency: boolean,
  ): DataPointResponse<DrawdownData> {
    let rolling: [Timestamp, number][] = [];
    if (useBaseCurrency && item.performanceBase && item.performanceBase.rolling && item.performanceBase.rolling[rollingInterval]) {
      rolling = item.performanceBase.rolling[rollingInterval];
    } else if (item.performance && item.performance.rolling && item.performance.rolling[rollingInterval]) {
      rolling = item.performance.rolling[rollingInterval];
    }

    const trimmedRolling = trimRollingData(rolling, startDate != null ? startDate.getTime() : 0, endDate.getTime());
    const data: DrawdownData = calculateDrawdownData(trimmedRolling);
    return {
      response: data,
      disclaimerKeys: [
        PROPOSAL_DISCLAIMERS.DRAWDOWN_ANALYSIS,
        PROPOSAL_DISCLAIMERS.SHORT_HISTORY_PORTFOLIOS,
        PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER,
      ],
    };
  }

  /**
   * Gets a comparison holding mer
   * @param {ComparisonHolding} holding the comparison holding to get the mer for
   */
  public static getComparisonHoldingMER(holding: ComparisonHolding): DataPointResponse<number | undefined> {
    const mer = holding.metrics?.mer != null ? holding.metrics.mer : undefined;
    return { response: mer, disclaimerKeys: mer ? [PROPOSAL_DISCLAIMERS.FUNDATA_MERS] : [] };
  }

  /**
   * Gets a comparison holding yield
   * @param {ComparisonHolding} holding the comparison holding to get the mer for
   */
  public static getComparisonHoldingYield(holding: ComparisonHolding): DataPointResponse<Dictionary<number | undefined>> {
    return {
      response: {
        distributionYield: holding.metrics?.distributionYield,
        yieldToMaturity: holding.metrics?.yieldToMaturity?.value,
        totalYield: holding.metrics?.totalYield,
      },
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.RETURN_ON_EQUITY, PROPOSAL_DISCLAIMERS.EQUITY_OTHER_METRICS, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets a comparison holding weight
   * @param {ComparisonHolding} holding the comparison holding to get the weight for
   */
  public static getComparisonHoldingWeight(holding: ComparisonHolding): number {
    return holding.weight;
  }

  /**
   * Gets a comparison holding market value
   * @param {ComparisonHolding} holding the comparison holding to get the weight for
   */
  public static getComparisonHoldingMarketValue(holding: ComparisonHolding, capital: number): number {
    return holding.weight * capital;
  }

  /**
   * Gets the percent growths returns for a value
   * @param {ComparisonHolding} holding the holding to get the percent growth return for
   */
  public static getHoldingPercentGrowths(holding: ComparisonHolding, useBaseCurrency: boolean = false): DataPointResponse<PercentGrowth> {
    const { metrics } = holding;

    let growth: PercentGrowth | undefined;
    if (metrics != null) {
      growth = useBaseCurrency && metrics.basePercentGrowth != null ? metrics.basePercentGrowth : metrics.percentGrowth;
    }

    if (growth == null) {
      const nullPercentGrowth: PercentGrowth = {
        ...DEFAULT_GROWTH_VALUES,
      };
      return { response: nullPercentGrowth, disclaimerKeys: [] };
    }

    const disclaimerKeys: Array<string> = [
      PROPOSAL_DISCLAIMERS.NET_FUND_FEES_PERFORMANCE,
      PROPOSAL_DISCLAIMERS.PERFORMANCE_PERIOD,
      PROPOSAL_DISCLAIMERS.SHORT_HISTORY_PORTFOLIOS,
      PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER,
    ];

    return { response: growth, disclaimerKeys };
  }

  /**
   * Gets the percent growth for an item by period
   * @param item the proposal item to get the percent growth for
   * @param period the period to get the return for
   */
  public static getHoldingPercentGrowthByPeriod(holding: ComparisonHolding, period: GrowthTypes, useBaseCurrency: boolean = false): number | null {
    const growth: DataPointResponse<PercentGrowth> = DataPoints.getHoldingPercentGrowths(holding, useBaseCurrency);
    return growth.response[period];
  }

  /**
   * Groups a comparison item's underlying holdings by the major asset group (Cash,Equity, Fixed Income or Other)
   * @param item the comparison item to get the holdings for
   */
  public static getHoldingsByAssetGroup(item: ComparisonItem, hasClassEnabled?: boolean): DataPointResponse<Dictionary<ComparisonHolding[]>> {
    const defaultValue: Dictionary<ComparisonHolding[]> = {
      [ASSET_GROUPS.CASH]: [],
      [ASSET_GROUPS.FIXED_INCOME]: [],
      [ASSET_GROUPS.EQUITY]: [],
      [ASSET_GROUPS.ALTERNATIVE_INVESTMENT]: [],
      [ASSET_GROUPS.MIXED_ASSETS]: [],
      [ASSET_GROUPS.REAL_ASSETS]: [],
      [ASSET_GROUPS.OTHER]: [],
    };
    const dpResponse = DataPoints.getItemHoldings(item, hasClassEnabled);
    const response = dpResponse.response.reduce((acc, current) => {
      switch (current.assetGroup) {
        case ASSET_GROUPS.EQUITY: {
          acc[ASSET_GROUPS.EQUITY].push(current);
          break;
        }
        case ASSET_GROUPS.FIXED_INCOME: {
          acc[ASSET_GROUPS.FIXED_INCOME].push(current);
          break;
        }
        case ASSET_GROUPS.OTHER: {
          acc[ASSET_GROUPS.OTHER].push(current);
          break;
        }
        case ASSET_GROUPS.CASH: {
          acc[ASSET_GROUPS.CASH].push(current);
          break;
        }
        case ASSET_GROUPS.ALTERNATIVE_INVESTMENT: {
          acc[ASSET_GROUPS.ALTERNATIVE_INVESTMENT].push(current);
          break;
        }
        case ASSET_GROUPS.MIXED_ASSETS: {
          acc[ASSET_GROUPS.MIXED_ASSETS].push(current);
          break;
        }
        case ASSET_GROUPS.REAL_ASSETS: {
          acc[ASSET_GROUPS.REAL_ASSETS].push(current);
          break;
        }
        default: {
          acc[ASSET_GROUPS.OTHER].push(current);
        }
      }
      return acc;
    }, defaultValue);

    return { response, disclaimerKeys: dpResponse.disclaimerKeys };
  }

  /**
   * Groups a comparison item's underlying holdings by the major asset group (Cash,Equity, Fixed Income or Other)
   * @param item the comparison item to get the holdings for
   */
  public static getFundHoldingsByAssetGroup(item: ComparisonItem): DataPointResponse<Dictionary<FundAllocation[]>> {
    const response: Dictionary<FundAllocation[]> = {
      [ASSET_GROUPS.CASH]: [],
      [ASSET_GROUPS.FIXED_INCOME]: [],
      [ASSET_GROUPS.EQUITY]: [],
      [ASSET_GROUPS.ALTERNATIVE_INVESTMENT]: [],
      [ASSET_GROUPS.MIXED_ASSETS]: [],
      [ASSET_GROUPS.REAL_ASSETS]: [],
      [ASSET_GROUPS.OTHER]: [],
    };

    if (item.detailedHoldings && item.detailedHoldings.funds) {
      for (const fund of item.detailedHoldings.funds) {
        const assetGroup = fund.assetGroup || ASSET_GROUPS.OTHER;
        const currentGroup = response[assetGroup] || response[ASSET_GROUPS.OTHER];
        currentGroup.push(fund);
      }
    }

    return { response, disclaimerKeys: [] };
  }

  /**
   * Groups a comparison item's underlying holdings by the major asset group (Cash,Equity, Fixed Income or Other)
   * @param item the comparison item to get the holdings for
   */
  public static getLipperLeaderScores(item: ComparisonItem): DataPointResponse<LipperLeaders | undefined> {
    const { lipperLeaders } = item;

    if (
      lipperLeaders === undefined ||
      (lipperLeaders.consistentReturn === undefined && lipperLeaders.preservation === undefined && lipperLeaders.totalReturn === undefined)
    ) {
      return { response: undefined, disclaimerKeys: [] };
    }

    return {
      response: lipperLeaders,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.LIPPER_AS_AT, PROPOSAL_DISCLAIMERS.LIPPER_LEADERS, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the income stream senariios on a comparison item
   * @param item the comparison item to get the income stream scenarios for
   */
  public static getIncomeStreamData(item: ComparisonItem): DataPointResponse<IncomeStreamScenarios | undefined> {
    const { scenarios } = item;

    if (scenarios === undefined) {
      return { response: undefined, disclaimerKeys: [] };
    }

    return {
      response: scenarios,
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.LIPPER_AS_AT, PROPOSAL_DISCLAIMERS.LIPPER_LEADERS, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the rebalance analysis data for a current item compared to a recommended item (target).
   * @param currentItem The item that represents the current portfolio.
   * @param recommendedItem The item that represents the recommended portfolio.
   */
  public static getRebalanceAnalysisData(currentItem: ComparisonItem, recommendedItem: ComparisonItem): DataPointResponse<RebalanceAnalysisData> {
    const mergeDuplicateHoldings = (holdings: ComparisonHolding[]) =>
      deepClone(holdings).reduce((reducedHoldings, holding) => {
        const existingHoldingIndex = reducedHoldings.findIndex(h => h.id === holding.id);
        if (existingHoldingIndex > -1) {
          reducedHoldings[existingHoldingIndex].weight += holding.weight;
        } else {
          reducedHoldings.push(holding);
        }

        return reducedHoldings;
      }, [] as ComparisonHolding[]);

    const currentPortfolioDpResponse: DataPointResponse<ComparisonHolding[]> = DataPoints.getItemHoldings(currentItem);
    const currentHoldings = mergeDuplicateHoldings(currentPortfolioDpResponse.response);

    const recommendedPortfolio = DataPoints.getItemHoldings(recommendedItem);
    const recommendedPortfolioHoldings = mergeDuplicateHoldings(recommendedPortfolio.response);

    const rebalance = recommendedPortfolioHoldings.map(rebalancedHolding => {
      const currentHolding = currentHoldings.find(a => a.id === rebalancedHolding.id) ?? { weight: 0 };

      const currentAmount = ((currentItem.size || 0) * currentHolding.weight) / 100;
      const rebalancedAmount = ((currentItem.size || 0) * rebalancedHolding.weight) / 100;

      return {
        id: rebalancedHolding.id,
        name: rebalancedHolding.name,
        weight: currentHolding.weight,
        amount: currentAmount,
        targetWeight: rebalancedHolding.weight,
        targetAmount: rebalancedAmount,
        weightAdjustment: rebalancedHolding.weight - currentHolding.weight,
        amountAdjustment: rebalancedAmount - currentAmount,
      };
    });

    // Find the difference between current and recommended portfolio
    const divestedHoldings = currentHoldings.filter(a => !recommendedPortfolioHoldings.some(b => a.id === b.id));

    const divest = divestedHoldings.map(holding => {
      const currentAmount = ((currentItem.size || 0) * holding.weight) / 100;

      return {
        id: holding.id,
        name: holding.name,
        weight: holding.weight,
        amount: currentAmount,
        targetWeight: 0,
        targetAmount: 0,
        weightAdjustment: -holding.weight,
        amountAdjustment: -currentAmount,
      };
    });

    return {
      response: {
        rebalance,
        divest,
      },
      disclaimerKeys: [],
    };
  }

  /**
   * Gets the ESG carbon intensity data for an item for displaying on a proposal
   * @param item
   */

  public static getESGCarbonIntensityData(item: ESGInstrument): DataPointResponse<ESGCarbonIntensityDetails> {
    if (!item.esg?.carbonIntensity) {
      return {
        response: {
          value: null,
          transposedValue: null,
          key: 'NA',
        },
        disclaimerKeys: [],
      };
    }

    const carbonIntensity = parseFloat(item.esg.carbonIntensity.toFixed(1));

    const output = {
      response: {
        value: carbonIntensity,
      },
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.ESG_CARBON_INTENSITY, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };

    switch (true) {
      case carbonIntensity < CarbonIntensityLimit.VERY_LOW:
        return {
          ...output,
          response: {
            ...output.response,
            transposedValue: 1,
            key: CARBON_INTENSITY.VERY_LOW,
          },
        };
      case carbonIntensity < CarbonIntensityLimit.LOW:
        return {
          ...output,
          response: {
            ...output.response,
            transposedValue: 2,
            key: CARBON_INTENSITY.LOW,
          },
        };
      case carbonIntensity < CarbonIntensityLimit.MODERATE:
        return {
          ...output,
          response: {
            ...output.response,
            transposedValue: 3,
            key: CARBON_INTENSITY.MODERATE,
          },
        };

      case carbonIntensity < CarbonIntensityLimit.HIGH:
        return {
          ...output,
          response: {
            ...output.response,
            transposedValue: 4,
            key: CARBON_INTENSITY.HIGH,
          },
        };

      default:
        return {
          ...output,
          response: {
            ...output.response,
            transposedValue: 5,
            key: CARBON_INTENSITY.VERY_HIGH,
          },
        };
    }
  }

  /**
   * Gets the ESG overview data for displaying on a proposal
   * @param item
   */
  public static getESGOverviewData(item: ESGInstrument): DataPointResponse<ESGOverviewDetails> {
    if (!item.esg?.rating) {
      return {
        response: {
          classification: undefined,
          score: undefined,
          value: undefined,
          peerRank: undefined,
          globalRank: undefined,
        },
        disclaimerKeys: [],
      };
    }

    const { classification, score, value } = item.esg.rating;
    const peerRank = item.esg.peerRank ? parseFloat(item.esg.peerRank.toFixed(0)) : undefined;
    const globalRank = item.esg.globalRank ? parseFloat(item.esg.globalRank.toFixed(0)) : undefined;

    return {
      response: { classification, score, value, peerRank, globalRank },
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.ESG_OVERVIEW, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };
  }

  /**
   * Gets the item ESG Implied Temperature Rise data for displaying on a proposal
   * @param item
   */
  public static getESGImpliedTemperatureRiseData(item: ESGInstrument): DataPointResponse<ESGTemperatureDetails> {
    if (!item.esg?.impliedTemperatureRise) {
      return {
        response: {
          value: null,
          transposedValue: null,
          key: 'UNAVAILABLE',
        },
        disclaimerKeys: [],
      };
    }

    const impliedTemperatureRise = parseFloat(item.esg.impliedTemperatureRise.toFixed(1));

    const output = {
      response: { value: impliedTemperatureRise },
      disclaimerKeys: [PROPOSAL_DISCLAIMERS.ESG_IMPLIED_TEMPERATURE_RISE, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER],
    };

    switch (true) {
      case impliedTemperatureRise <= ImpliedTemperatureLimit.ALIGNED_LOW:
        return {
          ...output,
          response: {
            ...output.response,
            transposedValue: 1,
            key: TEMPERATURE_RANKING.ALIGNED_LOW,
          },
        };
      case impliedTemperatureRise <= ImpliedTemperatureLimit.ALIGNED:
        return {
          ...output,
          response: {
            ...output.response,
            transposedValue: 1,
            key: TEMPERATURE_RANKING.ALIGNED,
          },
        };
      case impliedTemperatureRise <= ImpliedTemperatureLimit.MISALIGNED:
        return {
          ...output,
          response: {
            ...output.response,
            transposedValue: 2,
            key: TEMPERATURE_RANKING.MISALIGNED,
          },
        };
      case impliedTemperatureRise > ImpliedTemperatureLimit.MISALIGNED:
        return {
          ...output,
          response: {
            ...output.response,
            transposedValue: 3,
            key: TEMPERATURE_RANKING.STRONGLY_MISALIGNED,
          },
        };
      default:
        return {
          ...output,
          response: {
            ...output.response,
            transposedValue: 3,
            key: TEMPERATURE_RANKING.UNAVAILABLE,
          },
        };
    }
  }

  /**
   * Gets the AltMetrics data for an item
   * @param {ComparisonItem} item the proposal item to get the alt metrics for
   */
  public static getAltMetrics(item: ComparisonItem): DataPointResponse<AltMetricsDetails> {
    const itemType = item.type;

    let response = {} as AltMetricsDetails;

    if (itemType !== COMPARISON_ITEM_TYPES.ALT && itemType !== COMPARISON_ITEM_TYPES.PORTFOLIO) {
      return { response, disclaimerKeys: [] };
    }

    const {
      manufacturer,
      vintage,
      status,
      commitment,
      called,
      distributed,
      remaining,
      managementFee,
      chargeFrequency,
      hurdleRate,
      carriedInterest,
      gpCatchUpRate,
      fundFormationCost,
      netIrr,
      medianBenchmarkNetIrr,
      quartile,
    } = item;

    response = {
      manufacturer,
      vintage,
      status,
      commitment,
      called,
      distributed,
      remaining,
      managementFee,
      chargeFrequency,
      hurdleRate,
      carriedInterest,
      gpCatchUpRate,
      fundFormationCost,
      netIrr,
      medianBenchmarkNetIrr,
      quartile,
    };

    const disclaimerKeys: Array<string> = [PROPOSAL_DISCLAIMERS.PERFORMANCE_PERIOD, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER];

    return { response, disclaimerKeys };
  }

  public static getHistoricalScenariosMetrics(item: ComparisonItem): DataPointResponse<HistoricalScenariosComparisonItemPerformance | undefined> {
    return { response: item.historicalScenarios, disclaimerKeys: [] };
  }

  /**
   * Gets the current portfolio growth period for a proposal
   * @param {Proposal} proposal the proposal to get the growth period
   */
  public static getPortfolioGrowthPeriod(proposal: Proposal): TIME_PERIODS | undefined {
    const { comparisonToolbarOptions } = proposal;
    const portfolioGrowth = comparisonToolbarOptions?.portfolioGrowth;
    const period = (portfolioGrowth as { period?: TIME_PERIODS })?.period;
    const startDate = portfolioGrowth?.startDate;
    const endDate = portfolioGrowth?.endDate;

    if (period || (startDate && endDate)) {
      if (!period && startDate && endDate) {
        const months = monthsDiff(startDate, endDate);
        return monthsToPeriod(months);
      }
      return period;
    }

    return undefined;
  }

  /**
   * Check if the growth meets the target rate of return in a proposal
   * @param {Proposal} proposal the proposal to get current return period
   * @param {PercentGrowth} percentGrowth the percentGrowth of the proposal item
   * @param {number} target the plan target rate of return
   */
  public static isGrowthOnTrack(proposal: Proposal, percentGrowth?: PercentGrowth, target?: number): boolean {
    const returnPeriod = proposal.toolbarOptions[DAOType.PlanningAnnualizedRoi]?.period ?? DataPoints.getPortfolioGrowthPeriod(proposal);
    if (!percentGrowth || !returnPeriod || !target) return false;
    const growth = percentGrowth[returnPeriod];
    return !!(growth && growth >= target);
  }

  /**
   * Check if the asset allocation is aligned with the plan risk profile's asset allocation rule
   * @param {User} user the user from the proposal
   * @param {ComparisonItem} item the proposal item to get the risk profile rule
   * @param {Account} account the account with the risk profile rules
   */
  public static isAssetAllocationOnTrack(user: User, item: ComparisonItem, account?: Account): boolean {
    if (!account?.riskProfile?.rules) return false;

    const { rules } = account.riskProfile;

    // "all rules must be on track"
    return rules.every(rule => {
      // validate the rule Asset Allocation exclusively
      if (rule.ruleType !== RiskProfileRuleType.AssetAllocation) return true;

      const rulesData = getRiskProfileRuleData(rule, item, undefined, user);
      const filteredData = rulesData.filter(rD => !rD.hidden);

      return !rule.value.some(
        rV =>
          // only search for data that has an adjustment value
          !!filteredData.some(
            fD =>
              rV.assetType === fD.key &&
              fD.value != null &&
              fD.adjustment !== undefined && // explicitly check for undefined to avoid issues with 0
              fD.adjustment !== null && // explicitly check for null
              fD.adjustment !== 0, // check if adjustment is non-zero
          ),
      );
    });
  }

  /**
   * Gets the tax analysis metrics for an item (tax implications)
   * @param {ComparisonItem} item the item to retrieve the tax analysis for
   */
  public static getTaxAnalysisMetrics(item: ComparisonItem): DataPointResponse<PortfolioTaxMetrics | undefined> {
    if (item.type !== COMPARISON_ITEM_TYPES.PORTFOLIO) {
      return { response: undefined, disclaimerKeys: [] };
    }

    const taxMetrics = item.taxAnalysis?.taxMetrics;

    if (!taxMetrics) {
      return { response: undefined, disclaimerKeys: [] };
    }

    return {
      response: taxMetrics,
      disclaimerKeys: [],
    };
  }
}
